import React, { useState, useEffect } from "react"; 
import axios from "axios";
import API_BASE_URL from "../config";
import "./LeaveManagement.css";

// React Icons Imports
import {
  FaCalendarAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
  FaCalendarDay,
  FaCalendarCheck,
  FaClock,
  FaSync,
  FaFileAlt,
  FaListAlt,
  FaSpinner,
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaArrowRight,
  FaArrowLeft,
  FaPlus,
  FaTimes,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaHourglassHalf,
  FaCalendarTimes,
  FaInfoCircle,
  FaUserTie,
  FaStethoscope,
  FaBaby,
  FaHospital,
  FaHeart,
  FaBell,
  FaBuilding,
  FaHome,
  FaBriefcase,
  FaPaperPlane
} from 'react-icons/fa';

const LeaveManagement = ({ employee }) => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: "",
    employeeName: "",
    leaveType: "Casual Leave",
    startDate: "",
    endDate: "",
    reason: "",
    contactDuringLeave: "",
    addressDuringLeave: "",
    reportingManager: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Get authentication token
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token');
  };

  // Configure axios with auth headers
  const createApiClient = () => {
    const token = getAuthToken();
    return axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  // Auto-fill employee details and fetch leave requests
  useEffect(() => {
    if (employee && employee.emp_id) {
      setFormData(prev => ({
        ...prev,
        employeeId: employee.emp_id,
        employeeName: employee.name || "",
        reportingManager: employee.reporting_manager || ""
      }));
      fetchLeaveRequests(employee.emp_id);
    }
  }, [employee]);

  const fetchLeaveRequests = async (empId) => {
    try {
      setLoading(true);
      const apiClient = createApiClient();
      const response = await apiClient.get(`/api/leave`, {
        params: { employeeId: empId }
      });
      setLeaveRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      if (error.response?.status === 401) {
        setMessage({
          type: "error",
          text: "Authentication failed. Please log in again."
        });
      } else {
        setMessage({
          type: "error",
          text: "Failed to load leave requests. Please try again."
        });
      }
      setLeaveRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateLeaveDays = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Validate dates
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setMessage({
        type: "error",
        text: "End date cannot be before start date."
      });
      setLoading(false);
      return;
    }

    try {
      const apiClient = createApiClient();
      const leaveData = {
        ...formData,
        leaveDays: calculateLeaveDays(),
        applicationDate: new Date().toISOString(),
        status: "Pending"
      };

      const response = await apiClient.post('/api/leave', leaveData);
      
      setMessage({
        type: "success",
        text: response.data.message || "Leave request submitted successfully!"
      });

      // Reset form
      setFormData({
        employeeId: employee.emp_id || "",
        employeeName: employee.name || "",
        leaveType: "Casual Leave",
        startDate: "",
        endDate: "",
        reason: "",
        contactDuringLeave: "",
        addressDuringLeave: "",
        reportingManager: employee.reporting_manager || ""
      });

      setShowLeaveForm(false);
      fetchLeaveRequests(employee.emp_id);

    } catch (error) {
      console.error("Error submitting leave request:", error);
      
      if (error.response?.status === 401) {
        setMessage({
          type: "error",
          text: "Session expired. Please log in again."
        });
      } else if (error.response?.data?.message) {
        setMessage({
          type: "error",
          text: error.response.data.message
        });
      } else {
        setMessage({
          type: "error",
          text: "Failed to submit leave request. Please check your connection and try again."
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Pending": { 
        class: "leavi-status-pending", 
        text: "Pending", 
        icon: <FaHourglassHalf />
      },
      "Approved": { 
        class: "leavi-status-approved", 
        text: "Approved", 
        icon: <FaCheckCircle />
      },
      "Rejected": { 
        class: "leavi-status-rejected", 
        text: "Rejected", 
        icon: <FaTimesCircle />
      }
    };
    const config = statusConfig[status] || statusConfig.Pending;
    return (
      <span className={`leavi-status-badge ${config.class}`}>
        <span className="leavi-status-icon">{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowLeaveForm(false);
    }
  };

  // Get icon for leave type
  const getLeaveTypeIcon = (type) => {
    switch(type) {
      case 'Casual Leave':
        return <FaBriefcase />;
      case 'Sick Leave':
        return <FaStethoscope />;
      case 'Earned Leave':
        return <FaCalendarCheck />;
      case 'Maternity Leave':
        return <FaBaby />;
      case 'Paternity Leave':
        return <FaBaby />;
      case 'Emergency Leave':
        return <FaHospital />;
      default:
        return <FaCalendarAlt />;
    }
  };

  return (
    <div className="leavi-container">
      <div className="leavi-header">
        <div className="leavi-header-content">
          <div className="leavi-title-section">
            <h1 className="leavi-main-title">Leave Management</h1>
            <p className="leavi-subtitle">Manage your leave requests and track their status</p>
          </div>
          <button 
            className="leavi-primary-btn" 
            onClick={() => setShowLeaveForm(true)}
            disabled={!employee?.reporting_manager}
          >
            <span className="leavi-btn-icon">
              <FaCalendarAlt />
            </span>
            Apply for Leave
          </button>
        </div>
      </div>

      {!employee?.reporting_manager && (
        <div className="leavi-warning-message">
          <span className="leavi-warning-icon">
            <FaExclamationTriangle />
          </span>
          <div className="leavi-warning-content">
            <strong>Reporting Manager Required</strong>
            <p>You don't have a reporting manager assigned. Please contact HR to apply for leave.</p>
          </div>
        </div>
      )}

      {message.text && (
        <div className={`leavi-message leavi-message-${message.type}`}>
          <span className="leavi-message-icon">
            {message.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          </span>
          {message.text}
        </div>
      )}

      {showLeaveForm && (
        <div className="leavi-modal-overlay" onClick={handleOverlayClick}>
          <div className="leavi-modal-container">
            <div className="leavi-modal-header">
              <div className="leavi-modal-title-section">
                <h2 className="leavi-modal-title">Apply for Leave</h2>
                <p className="leavi-modal-subtitle">Fill in the details below to submit your leave request</p>
              </div>
              <button 
                className="leavi-close-btn" 
                onClick={() => setShowLeaveForm(false)}
                disabled={loading}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="leavi-form">
              <div className="leavi-form-section">
                <h3 className="leavi-form-section-title">
                  <FaUser /> Employee Information
                </h3>
                <div className="leavi-form-grid">
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Employee ID</label>
                    <input 
                      type="text" 
                      value={formData.employeeId} 
                      disabled 
                      className="leavi-disabled-field" 
                    />
                  </div>
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Employee Name</label>
                    <input 
                      type="text" 
                      value={formData.employeeName} 
                      disabled 
                      className="leavi-disabled-field" 
                    />
                  </div>
                  <div className="leavi-form-group leavi-full-width">
                    <label className="leavi-form-label">Reporting Manager</label>
                    <input 
                      type="text" 
                      value={formData.reportingManager || "Not assigned"} 
                      disabled 
                      className="leavi-disabled-field" 
                    />
                  </div>
                </div>
              </div>

              <div className="leavi-form-section">
                <h3 className="leavi-form-section-title">
                  <FaCalendarAlt /> Leave Details
                </h3>
                <div className="leavi-form-grid">
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Leave Type <span className="leavi-required">*</span></label>
                    <select 
                      name="leaveType" 
                      value={formData.leaveType} 
                      onChange={handleInputChange} 
                      required
                      disabled={loading}
                      className="leavi-form-select"
                    >
                      <option value="Casual Leave">
                        <FaBriefcase /> Casual Leave
                      </option>
                      <option value="Sick Leave">
                        <FaStethoscope /> Sick Leave
                      </option>
                      <option value="Earned Leave">
                        <FaCalendarCheck /> Earned Leave
                      </option>
                      <option value="Maternity Leave">
                        <FaBaby /> Maternity Leave
                      </option>
                      <option value="Paternity Leave">
                        <FaBaby /> Paternity Leave
                      </option>
                      <option value="Emergency Leave">
                        <FaHospital /> Emergency Leave
                      </option>
                    </select>
                  </div>
                  
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Start Date <span className="leavi-required">*</span></label>
                    <input 
                      type="date" 
                      name="startDate" 
                      value={formData.startDate} 
                      onChange={handleInputChange} 
                      required 
                      min={new Date().toISOString().split('T')[0]} 
                      disabled={loading}
                      className="leavi-form-input"
                    />
                  </div>
                  
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">End Date <span className="leavi-required">*</span></label>
                    <input 
                      type="date" 
                      name="endDate" 
                      value={formData.endDate} 
                      onChange={handleInputChange} 
                      required 
                      min={formData.startDate || new Date().toISOString().split('T')[0]} 
                      disabled={loading}
                      className="leavi-form-input"
                    />
                  </div>
                </div>

                {formData.startDate && formData.endDate && (
                  <div className="leavi-leave-info">
                    <div className="leavi-days-count">
                      <span className="leavi-days-number">{calculateLeaveDays()}</span>
                      <span className="leavi-days-label">Total Leave Days</span>
                    </div>
                    {formData.reportingManager && (
                      <div className="leavi-manager-notice">
                        <span className="leavi-notice-icon">
                          <FaUserTie />
                        </span>
                        This request will be sent to: <strong>{formData.reportingManager}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="leavi-form-section">
                <h3 className="leavi-form-section-title">
                  <FaInfoCircle /> Additional Information
                </h3>
                <div className="leavi-form-group">
                  <label className="leavi-form-label">Reason for Leave <span className="leavi-required">*</span></label>
                  <textarea 
                    name="reason" 
                    value={formData.reason} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="Please provide a detailed reason for your leave application..." 
                    rows="3" 
                    disabled={loading}
                    className="leavi-form-textarea"
                  />
                </div>

                <div className="leavi-form-grid">
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Contact During Leave</label>
                    <input 
                      type="tel" 
                      name="contactDuringLeave" 
                      value={formData.contactDuringLeave} 
                      onChange={handleInputChange} 
                      placeholder="Emergency contact number" 
                      disabled={loading}
                      className="leavi-form-input"
                    />
                  </div>
                  <div className="leavi-form-group">
                    <label className="leavi-form-label">Address During Leave</label>
                    <textarea 
                      name="addressDuringLeave" 
                      value={formData.addressDuringLeave} 
                      onChange={handleInputChange} 
                      placeholder="Your address during leave period..." 
                      rows="2" 
                      disabled={loading}
                      className="leavi-form-textarea"
                    />
                  </div>
                </div>
              </div>

              <div className="leavi-form-actions">
                <button 
                  type="button" 
                  className="leavi-secondary-btn" 
                  onClick={() => setShowLeaveForm(false)}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button 
                  type="submit" 
                  className="leavi-primary-btn" 
                  disabled={loading || !formData.reportingManager}
                >
                  {loading ? (
                    <>
                      <span className="leavi-loading-spinner">
                        <FaSpinner className="spinning" />
                      </span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> Submit Leave Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="leavi-content">
        <div className="leavi-history-section">
          <div className="leavi-history-header">
            <h2 className="leavi-history-title">
              <FaListAlt /> My Leave Requests
              <span className="leavi-requests-count">({leaveRequests.length})</span>
            </h2>
            <button 
              className="leavi-refresh-btn"
              onClick={() => fetchLeaveRequests(employee?.emp_id)}
              disabled={loading}
            >
              <span className="leavi-refresh-icon">
                <FaSync />
              </span>
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="leavi-loading-state">
              <div className="leavi-loading-spinner-large">
                <FaSpinner className="spinning" />
              </div>
              <p>Loading leave requests...</p>
            </div>
          ) : leaveRequests.length > 0 ? (
            <div className="leavi-requests-container">
              <div className="leavi-requests-table">
                <table className="leavi-table">
                  <thead className="leavi-table-header">
                    <tr>
                      <th className="leavi-table-th">
                        <FaCalendarDay /> Application Date
                      </th>
                      <th className="leavi-table-th">
                        <FaFilter /> Leave Type
                      </th>
                      <th className="leavi-table-th">
                        <FaCalendarAlt /> Start Date
                      </th>
                      <th className="leavi-table-th">
                        <FaCalendarCheck /> End Date
                      </th>
                      <th className="leavi-table-th">
                        <FaClock /> Days
                      </th>
                      <th className="leavi-table-th">
                        <FaHourglassHalf /> Status
                      </th>
                      <th className="leavi-table-th">
                        <FaInfoCircle /> Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody className="leavi-table-body">
                    {leaveRequests.map((request, index) => (
                      <tr key={request._id || index} className="leavi-table-row">
                        <td className="leavi-table-td">
                          <div className="leavi-date-cell">
                            <FaCalendarDay /> {formatDate(request.applicationDate)}
                          </div>
                        </td>
                        <td className="leavi-table-td">
                          <span className="leavi-leave-type">
                            {getLeaveTypeIcon(request.leaveType)}
                            {request.leaveType}
                          </span>
                        </td>
                        <td className="leavi-table-td">
                          <div className="leavi-date-cell">
                            <FaCalendarAlt /> {formatDate(request.startDate)}
                          </div>
                        </td>
                        <td className="leavi-table-td">
                          <div className="leavi-date-cell">
                            <FaCalendarCheck /> {formatDate(request.endDate)}
                          </div>
                        </td>
                        <td className="leavi-table-td">
                          <span className="leavi-days-badge">
                            <FaCalendarTimes /> {request.leaveDays}
                          </span>
                        </td>
                        <td className="leavi-table-td">
                          {getStatusBadge(request.status)}
                        </td>
                        <td className="leavi-table-td leavi-reason-cell" title={request.reason}>
                          {request.reason.length > 50 ? `${request.reason.substring(0, 50)}...` : request.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="leavi-empty-state">
              <div className="leavi-empty-icon">
                <FaFileAlt />
              </div>
              <h3 className="leavi-empty-title">No Leave Requests</h3>
              <p className="leavi-empty-text">You haven't submitted any leave requests yet.</p>
              <button 
                className="leavi-primary-btn"
                onClick={() => setShowLeaveForm(true)}
                disabled={!employee?.reporting_manager}
              >
                <FaPlus /> Apply for Your First Leave
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveManagement;